import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, first, map, mergeMap, take, tap } from 'rxjs/operators';

import { AccessTypeE, PristupyVysledekDto, RestUzivatelService } from '@generated/be';
import { DEFAULT_SESSION_TIME } from '../constants/app.constants';
import { HttpStatus } from '../constants/http.constants';
import { EnvironmentName } from '../environments/environment.model';
import { Logger, LoggerService } from '../logger';
import { SKIP_ERROR_INTERCEPTOR_HEADER_KEY } from '../services/error-api.interceptor';
import { StorageService } from '../services/storage.service';
import { UIService } from '../services/ui.service';
import {
  AUTH_BASE_URL,
  CMS2_DOMAIN_SUFFIX,
  GET_TOKEN_PATH,
  INT_AUTH_BASE_URL,
  IVP_AUTH_BASE_URL,
  LOGIN_PATH,
  LOGOUT_PATH,
  PP_AUTH_BASE_URL,
  TP_AUTH_BASE_URL,
  VALIDATE_TOKEN_PATH,
} from './auth.constants';
import { AuthToken, AuthTokenResponse, AuthUser } from './auth.models';
import {
  decodeAuthToken,
  getTokenExpirationDate,
  isTokenExpired,
  SKIP_REFRESH_JWT_TOKEN_INTERCEPTOR_HEADER_KEY,
} from './jwt-token.utils';
import { IdleTimeoutService } from '../services/idle-timeout.service';

/**
 * @export
 * @class AuthService
 *
 * This service takes care of authentication.
 */
@Injectable()
export class AuthService {
  private _http = inject(HttpClient);
  private _uiService = inject(UIService);
  private _storageService = inject(StorageService);

  private userSub$ = new BehaviorSubject<AuthUser | null>(null);
  user$ = this.userSub$.asObservable();
  user!: AuthUser;
  private readonly restUzivatelService = inject(RestUzivatelService);
  private readonly loggerService = inject(LoggerService);
  private readonly logger: Logger = this.loggerService.createLogger('AuthService');
  private idleTimeoutService = inject(IdleTimeoutService);

  constructor() {
    this._storageService.tokenRemoved$.subscribe(() => this._redirectToLogout());

    // Odchytává událost odhlášení z IdleTimeoutService a provede odhlášení uživatele.
    this.idleTimeoutService.logoutRequested$.subscribe(() => {
      this.logout();
    });
  }

  getAuthToken() {
    return this._storageService.getToken();
  }

  saveToken(token: string) {
    this._storageService.saveToken(token);
  }

  validateTicket(currentUrl: string, ticketParam: string) {
    return this._http.get(`${this._authUrl(VALIDATE_TOKEN_PATH)}?service=${currentUrl}&ticket=${ticketParam}`, {
      observe: 'response',
      responseType: 'text',
      // TGC cookie z CAS hostu se na cross-origin volání pošle jedině s withCredentials
      withCredentials: true,
    });
  }

  // Synchronní metoda pro získání aktuálního uživatele
  getCurrentUser(): AuthUser | null {
    return this.userSub$.getValue();
  }

  /**
   * Veřejná metoda – rozhodne, jakým způsobem získat token.
   */
  retrieveAndSaveAuthToken(fillUser = true, refresh = false): Observable<AuthTokenResponse> {
    return this._uiService.usesMockAuth
      ? this.retrieveAndSaveAuthTokenMock(fillUser)
      : this.retrieveAndSaveAuthTokenRest(fillUser, refresh);
  }

  // Asynchronní metoda pro získání aktuálního uživatele z rest operace
  retrieveAndSaveAuthTokenRest(fillUser = true, refresh = false): Observable<AuthTokenResponse> {
    return (
      this._http.post(
        this._authUrl(GET_TOKEN_PATH),
        { app: 'EGOV' },
        {
          headers: new HttpHeaders()
            .append(SKIP_ERROR_INTERCEPTOR_HEADER_KEY, 'true')
            .append(SKIP_REFRESH_JWT_TOKEN_INTERCEPTOR_HEADER_KEY, 'true'),
          // jwt se autentizuje TGC cookie z CAS hostu → na cross-origin nutné withCredentials
          withCredentials: true,
        }
      ) as Observable<AuthTokenResponse>
    ).pipe(
      mergeMap((tokenResponse: AuthTokenResponse) => {
        this.saveToken(tokenResponse.accessToken);

        if (!fillUser) {
          return of(tokenResponse); // okamžitě vracíme token
        }

        // počkáme na dokončení fillUser, která vrací Observable<void>
        return this.fillUser(tokenResponse).pipe(
          map(() => tokenResponse) // vrátíme původní tokenResponse dál v řetězci
        );
      }),
      catchError((e: HttpErrorResponse) => {
        if (e.status === HttpStatus.Unauthorized && refresh) {
          setTimeout(() => this.logout(), 3000);
        }

        return throwError(() => e);
      })
    );
  }

  fillUser(tokenResponse?: AuthTokenResponse): Observable<void> {
    const token = tokenResponse?.accessToken ?? (this.getAuthToken() as string);
    const authData: AuthToken = decodeAuthToken(token);

    const baseUser: AuthUser = {
      name: authData.givenName ?? 'Jméno není vyplněno',
      surname: authData.familyName,
      roles: authData.roles ?? [],
      login: authData.login,
      exp: new Date(authData.exp * 1_000),
      id: authData.personId,
      ovmKod: authData.ovmKod ?? '',
      accesses: [], // bude doplněno z RESTu
    };

    return this.getAccesses().pipe(
      take(1),
      catchError((error) => {
        console.error('Chyba při načítání uživatele:', error);
        return of({ pristupy: [] }); // fallback DTO
      }),
      tap((pristupyDto: PristupyVysledekDto) => {
        baseUser.accesses = pristupyDto.pristupy ?? [];
        this.user = baseUser;
        this.userSub$.next(this.user);
      }),
      map(() => void 0) // výsledný typ: Observable<void>
    );
  }

  /**
   * Sestaví URL pro CAS endpointy. CAS běží v každém nasazeném prostředí na vyhrazeném hostu (rpp-*)
   * mimo origin REZA appky, proto se volá absolutně. Jen local dev jede relativně přes `/auth` (proxy).
   * Vyhodnocuje se per volání – environment.json se načítá app initializerem, tj. až po vzniku služby.
   */
  private _authUrl(path: string): string {
    // Výjimka: na CMS2 doméně (*.cms2.cz) je CAS vystavený na stejné doméně pod /auth a hlavičku
    // X-JIPKAAS-CMS (výběr přihlašovacích dlaždic) přidává až cms2 ingress. Absolutní egon URL by
    // ingress obešla (žádné dlaždice); relativní (same-origin) volání to řeší a navíc odpadá CORS.
    if (this._uiService.location.hostname.endsWith(CMS2_DOMAIN_SUFFIX)) {
      return `${AUTH_BASE_URL}${path}`;
    }

    const absoluteBases: Partial<Record<EnvironmentName, string>> = {
      [EnvironmentName.Ivp]: IVP_AUTH_BASE_URL,
      [EnvironmentName.Int]: INT_AUTH_BASE_URL,
      [EnvironmentName.Tp]: TP_AUTH_BASE_URL,
      [EnvironmentName.Pp]: PP_AUTH_BASE_URL,
    };
    const base = absoluteBases[this._uiService.environmentService.environment.name] ?? AUTH_BASE_URL;
    return `${base}${path}`;
  }

  redirectToLogin(currentUrl = '') {
    const completeUrl = encodeURIComponent(this._uiService.location.origin + currentUrl);

    const loginUrl = `${this._authUrl(LOGIN_PATH)}?service=${completeUrl}&locale=cs`;
    // console.log('Redirecting to login URL:', loginUrl);
    this._uiService.location.assign(loginUrl);
  }

  isAuthTokenExpired(token: string) {
    return isTokenExpired(token);
  }

  removeAuthToken() {
    this._storageService.removeToken();
    this.idleTimeoutService.stopInactivityTimer();
  }

  isLoggedIn() {
    return Boolean(this._storageService.getIsLoggedIn());
  }

  saveIsLoggedIn() {
    this._storageService.saveIsLoggedIn();
  }

  logout() {
    this.removeAuthToken();
    this._storageService.removeIsLoggedIn();

    this._uiService.startLoading();
    this._redirectToLogout();
  }

  /**
   * Ověří čerstvost JWT tokenu synchronně.
   * Pokud token expiroval a od té doby uběhlo více než `sessionTime` sekund, provede logout.
   */
  validateTokenAgeOnStartup(): void {
    const token = this.getAuthToken();

    if (!token || token === '') {
      return;
    }

    try {
      const expirationDate = getTokenExpirationDate(token);

      if (!expirationDate) {
        this.logger.warn('Token nemá exp claim – nelze ověřit stáří');
        return;
      }

      const now = new Date();
      const ageInMilliseconds = now.getTime() - expirationDate.getTime();
      const ageInSeconds = Math.floor(ageInMilliseconds / 1000);

      if (expirationDate < now && ageInSeconds > this.sessionTime) {
        this.logger.warn(`Token expiroval před více než ${this.sessionTime / 60} minutami (${ageInSeconds} s)`);
        // this.removeAuthToken(); // todo uvidime jestli bude potreba mazat expirovany token
      }
    } catch (err) {
      this.logger.error('Chyba při dekódování JWT tokenu:', err);
      this.logout();
    }
  }

  /**
   * Zkontroluje autentizaci a načte uživatele.
   * Pokud má lokální storage platný token, ale IS_LOGGED_IN v session storage chybí
   * (typicky nově otevřená záložka/okno s veřejnou routou jako /dms),
   * obnoví příznak přihlášení, aby se navigace mezi taby chovala konzistentně.
   */
  checkAuthAndLoadUser(): void {
    const token = this.getAuthToken();
    if (!token || this.isAuthTokenExpired(token)) return;

    if (!this.isLoggedIn()) {
      this.saveIsLoggedIn();
    }
    this.fillUser().subscribe({
      error: (err) => console.error('fillUser error:', err),
    });
  }

  private _redirectToLogout() {
    // relogService = identifikace služby pro CAS, podle níž po odhlášení nabídne tlačítko „přihlásit
    // se znovu" (cílovou URL re-loginu bere CAS z registrované relogUrl dané služby).
    // MUSÍ být origin s KONCOVÝM LOMÍTKEM – CAS páruje hodnotu proti serviceId regexům tvaru
    // `^https://host/.*` a proti relogUrl (`https://host/`). Holý origin bez lomítka nematchne nic
    // (na INTu to prošlo jen díky benevolentnímu regexu `…local.*`) a relog „nefunguje".
    // Origin se bere z aktuální adresy → hodnota je správná i na CMS2 doméně (…cms2.cz/).
    const relogService = encodeURIComponent(`${this._uiService.location.origin}/`);
    this._uiService.location.assign(`${this._authUrl(LOGOUT_PATH)}?relogService=${relogService}`);
  }

  /**
   * Získá přístupy uživatele.
   *
   * @param ignoreCustomAccesses Ignoruje vlastní přístupy, pokud jsou nastaveny.
   * @returns Observable s PristupyVysledekDto.
   */
  private getAccesses(): Observable<PristupyVysledekDto> {
    // Použití přímo Observable vrácené `vypisPristupy`
    return this.restUzivatelService.vypisPristupy().pipe(
      map((res) => {
        // const changedResultDebug = res.pristupy?.filter((pristup) => !pristup.includes(AccessTypeE.Ciselniky) && !pristup.includes('ARCHIV'));
        // console.log(changedResultDebug);      Pouzivam pro debug pristupu na localhostu
        // return { pristupy: changedResultDebug };
        return res;
      }),
      first() // Zajištění, že se odebírá pouze první hodnota
    );
  }

  /**
   * Vytváří statickou mockovanou instanci `AuthTokenResponse` pro použití v lokálním vývojovém prostředí.
   *
   * Token je načten z konfiguračního objektu (`environment.secret.jwtToken`), zbytek struktury je
   * vyplněn prázdnými nebo výchozími hodnotami, aby odpovídal tvaru produkční odpovědi.
   *
   * Tato metoda slouží pro simulaci odpovědi backendu při autentizaci bez nutnosti volat reálný server.
   *
   * @returns Objekt `AuthTokenResponse` se simulovanými daty pro testovací a vývojové účely.
   */
  private buildMockToken(): AuthTokenResponse {
    const token = this._uiService.environmentService.environment.secret.jwtToken;

    return {
      accessToken: token,
      additionalAttributes: [],
      authInfo: {
        authType: '',
        dbId: null,
        dbType: null,
      },
      authType: '',
      dbId: null,
      dbType: null,
      cin: null,
      crlToken: '',
      deputy: null,
      familyName: '',
      givenName: '',
      name: '',
      ovmCin: null,
      ovmWorkplaceId: null,
      ovmWorkplaceName: null,
      pin: null,
    };
  }

  /**
   * Simuluje získání a uložení autentizačního tokenu pro vývojové účely (lokální prostředí).
   *
   * Token je načten z konfiguračního objektu (`environment.secret.jwtToken`) a následně uložen
   * pomocí `saveToken()`. Volitelně se provede i naplnění uživatelských údajů metodou `fillUser()`,
   * pokud je `fillUser` nastaveno na `true`.
   *
   * Používá se jako náhrada za reálné volání backendu během lokálního vývoje.
   *
   * @param fillUser Určuje, zda se mají po uložení tokenu načíst a naplnit uživatelské informace.
   *                 Výchozí hodnota je `true`.
   * @returns Observable s instancí `AuthTokenResponse`. Pokud dojde k chybě při naplňování uživatele,
   *          chyba je zalogována a znovu vyhozena.
   */
  private retrieveAndSaveAuthTokenMock(fillUser = true): Observable<AuthTokenResponse> {
    const tokenResp = this.buildMockToken();
    this.saveToken(tokenResp.accessToken);

    if (!fillUser) {
      return of(tokenResp);
    }

    return this.fillUser(tokenResp).pipe(
      map(() => tokenResp),
      catchError((error) => {
        this.logger.error('Chyba při získání a uložení mock auth tokenu:', error);
        throw error;
      })
    );
  }

  get sessionTime(): number {
    return this._uiService.environmentService.environment.defaultSessionTime ?? DEFAULT_SESSION_TIME;
  }
}
